import { reportError } from "@/utils/reportError";
import { supabase } from "@/utils/supabase";
import { useEffect, useState } from "react";

export interface Company {
  id: string;
  name: string;
}

export function useCompanies() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from("companies")
        .select("id, name")
        .order("name", { ascending: true });

      if (fetchError) {
        reportError(fetchError);
        throw fetchError;
      }

      setCompanies(data || []);
    } catch (err: any) {
      reportError(err);
      setError(err.message);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Ajoute une entreprise de façon idempotente (dédup insensible à la casse).
   * - Si le nom existe déjà (localement ou en base), retourne l'existante sans
   *   créer de doublon.
   * - Sinon insère la nouvelle ligne et retourne { id, name }.
   * Nécessite une session authentifiée (policy RLS INSERT sur companies).
   */
  const addCompany = async (name: string): Promise<Company | null> => {
    const trimmed = name.trim();
    if (!trimmed) return null;

    // Dédup côté client : évite un aller-retour si l'entreprise est déjà chargée.
    const local = companies.find(
      (c) => c.name.trim().toLowerCase() === trimmed.toLowerCase()
    );
    if (local) return local;

    try {
      const { data, error: insertError } = await supabase
        .from("companies")
        .insert({ name: trimmed })
        .select("id, name")
        .single();

      if (insertError) {
        // Course possible : un autre client vient d'insérer le même nom et
        // l'index unique lower(name) rejette. On relit alors l'existante
        // (insensible à la casse) au lieu de remonter une erreur.
        const { data: existing } = await supabase
          .from("companies")
          .select("id, name")
          .ilike("name", trimmed)
          .limit(1)
          .maybeSingle();

        if (existing) {
          setCompanies((prev) =>
            prev.some((c) => c.id === existing.id) ? prev : [...prev, existing]
          );
          return existing;
        }

        reportError(insertError);
        return null;
      }

      setCompanies((prev) =>
        [...prev, data].sort((a, b) => a.name.localeCompare(b.name))
      );
      return data;
    } catch (err: any) {
      reportError(err);
      return null;
    }
  };

  return {
    companies,
    loading,
    error,
    refreshCompanies: fetchCompanies,
    addCompany,
  };
}
